import { useState, useCallback, useMemo, useEffect } from 'react'
import { HotTable } from '@handsontable/react'
import { t } from '@lingui/core/macro'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import TextField from '@mui/material/TextField'
import { object, func, string, bool, array } from 'prop-types'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { LICENSE_KEY } from '@global/constants/handsontable'
import { useAppSelector } from '@redux/hooks'
import { toDataRowsFromListColumns } from 'functions'
import { ColumnDialog } from './column-dialog'
import { CommentDialog } from './comment-dialog'
import { getListValueId, getSafeDialogPosition, makeButtonRenderer } from './helpers'
import { ListEditor } from './list-editor'
import { TextRendererWithDataAttributes } from './list-table-cell-renderer'
import { appendRowColHeaderAttributes } from '../../../utils/append-row-col-header-attributes'
import { tempIdGenerator } from '../../../utils/temp-id-generator'
import { testInstanceHolder } from '../../../utils/test-instance-holder'
import './list-table.global.css' // don't delete this

/**
 * Create a new column; set the precedence, which determines the column order.
 * @param {*} params
 * @param {number} params.precedence The column sorting order.
 * @returns
 */
function getNewColumn({ precedence }) {
  return {
    id: tempIdGenerator('lc'),
    name: '',
    precedence,
    list_values: [{ id: tempIdGenerator('lv'), row_number: 0, value: null }],
  }
}

/**
 * Change the predence of a new column.
 * @param {*} params
 * @param {number} params.selectionStartCol The column start position.
 * @param {object} params.newColumn The new column object.
 * @returns
 */
function setNewColumnPrecedence({ selectionStartCol, newColumn }) {
  newColumn.precedence = selectionStartCol
  return newColumn
}

// Core table component that handles both Liveblocks and non-Liveblocks modes
export const ListTableCore = ({
  readOnly = false,
  list = {},
  setList,
  type = '',
  onChange = () => {},
  poweredByTemplate,
  searchTerm = '',
  setTableRef,
  tableRef,
  threads,
  activeCommentTarget,
  setActiveCommentTarget,
  checkPendingStatus,
  updateLocalThreadStatus,
  queueThreadResolution,
  queuedResolutionsRef,
  createThread,
  editThreadMetadata,
  isApproverMode = true,
}) => {
  const notDropdown = type !== 'drop-down'
  const gridContainer = document.getElementById('list-container')
  const [open, setOpen] = useState(false)
  const [isNewColumDialog, setIsNewColumDialog] = useState(false)
  const [selectionCol, setSelectionCol] = useState(null)
  const [newName, setNewName] = useState('')
  const [renamingColumn, setRenamingColumn] = useState(null)
  const columnHeaders = useMemo(() => list?.list_columns?.map(lc => lc.name), [list.list_columns])
  const data = useMemo(
    () =>
      list?.list_columns?.length > 0 ? toDataRowsFromListColumns(list.list_columns, false) : [],
    [list?.list_columns],
  )
  const newColumn = getNewColumn({ precedence: 0 })
  const renamingColumnName = columnHeaders[renamingColumn]
  const renameDialogTitle =
    renamingColumn !== null ? t`Rename ${renamingColumnName}` : t`Rename column`

  const [ENABLE_LIVEBLOCKS = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.ENABLE_LIVEBLOCKS]),
  )

  const canUseLiveblocks = ENABLE_LIVEBLOCKS && createThread && editThreadMetadata

  const hasPendingThreads = checkPendingStatus(threads)

  const openCommentDialog = canUseLiveblocks
    ? (listValueId, position) => {
        setActiveCommentTarget({ id: listValueId, position })
      }
    : undefined

  useEffect(() => {
    if (!canUseLiveblocks) return
    const onMouseUp = e => {
      const root = tableRef?.current?.hotInstance.rootElement
      const clickedInsideTable = root?.contains(e.target)
      const clickedDialog = document.getElementById('comment-dialog-root')?.contains(e.target)

      if (!clickedInsideTable && !clickedDialog) {
        setActiveCommentTarget(null)
      }
    }
    document.addEventListener('mouseup', onMouseUp)
    return () => document.removeEventListener('mouseup', onMouseUp)
  }, [canUseLiveblocks, tableRef])

  useEffect(() => {
    if (!canUseLiveblocks) return
    tableRef?.current?.hotInstance.render()
  }, [canUseLiveblocks, activeCommentTarget])

  useEffect(() => {
    testInstanceHolder.register('edit-list-hot', tableRef?.current)
    setTableRef(tableRef)
  }, [])

  const ButtonRenderer = useMemo(
    () =>
      canUseLiveblocks
        ? makeButtonRenderer(list, activeCommentTarget?.id, threads, queuedResolutionsRef.current)
        : undefined,
    [canUseLiveblocks, activeCommentTarget?.id, threads, list, queuedResolutionsRef.current],
  )

  const addColumnHeaderDataAttributes = useCallback((colIdx, TH) => {
    appendRowColHeaderAttributes(TH, colIdx, null)
  }, [])

  const addRowHeaderDataAttributes = useCallback((rowIdx, th) => {
    appendRowColHeaderAttributes(th, null, rowIdx)
  }, [])

  const insertColumn = useCallback(
    (event, selection) => {
      setOpen(false)
      setIsNewColumDialog(false)
      setList(old => {
        const selectionStartCol = selection[0].start.col
        // If the selected column is at the end of the columns with data,
        // set its precedence to the correct position and add it to the list.
        const oldListColumns =
          old.list_columns.length === selectionStartCol
            ? [...old.list_columns, setNewColumnPrecedence({ selectionStartCol, newColumn })]
            : [...old.list_columns]
        const copy = oldListColumns.map(col => ({
          ...col,
          precedence: col.precedence >= selectionStartCol ? col.precedence + 1 : col.precedence,
        }))

        copy.splice(selectionStartCol, 0, {
          id: tempIdGenerator('lc'),
          precedence: selectionStartCol,
          name: newName || t`Column Name`,
          list_id: old.id,
          list_values: oldListColumns[selectionStartCol].list_values.map(() => ({
            row_number: 0,
            value: null,
          })),
        })

        return {
          ...old,
          list_columns: copy,
        }
      })
    },
    [setList, newName],
  )

  useEffect(() => {
    window.addEventListener('resize', updateHandsontableHeightAndWidth)
    function updateHandsontableHeightAndWidth() {
      const width = tableRef?.current?.hotInstance.rootElement.parentElement.offsetWidth
      const height = tableRef?.current?.hotInstance.rootElement.parentElement.offsetHeight
      tableRef?.current?.hotInstance.updateSettings({
        width,
        height,
      })
    }
    return () => {
      window.removeEventListener('resize', updateHandsontableHeightAndWidth)
    }
  }, [tableRef?.current])

  const moveToStart = (_, event) => {
    setList(old => {
      const copy = old.list_columns
      const movedColumn = old.list_columns.splice(event[0].start.col, 1)
      return {
        ...list,
        list_columns: [movedColumn[0], ...copy].map((lc, index) => ({ ...lc, precedence: index })),
      }
    })
  }

  // Rename a new column
  const renameNewColumn = useCallback((key, selections) => {
    setSelectionCol(selections)
    setIsNewColumDialog(true)
    setRenamingColumn(selections[0].start.col)
  }, [])

  const renameColumn = useCallback(
    (key, selections) => {
      setRenamingColumn(selections[0].start.col)
      setOpen(true)
    },
    [setRenamingColumn, setOpen],
  )

  const afterRemoveRows = (_, coordinates) => {
    setList(old => {
      const copy = [...old.list_columns].map(column => ({
        ...column,
        list_values: column.list_values
          .filter(
            lv =>
              lv.row_number > coordinates[0].end.row || lv.row_number < coordinates[0].start.row,
          )
          .map(lv => {
            const numRowsDeleted = coordinates[0].end.row - coordinates[0].start.row + 1
            return {
              ...lv,
              row_number:
                lv.row_number >= coordinates[0].end.row
                  ? lv.row_number - numRowsDeleted
                  : lv.row_number,
            }
          }),
      }))
      return { ...old, list_columns: copy }
    })
  }

  const afterAddRow = (_, coordinates) => {
    const start = coordinates[0].start.row
    setList(old => {
      const copy = [...old.list_columns].map(column => {
        // increment the row number for anything after the newly added row
        const values = [...column.list_values].map(row => ({
          ...row,
          row_number: row.row_number >= start ? row.row_number + 1 : row.row_number,
        }))

        // add the new list value
        values.push({ row_number: start, value: '' })
        return {
          ...column,
          list_values: values,
        }
      })
      return { ...old, list_columns: copy }
    })
  }

  const contextMenu = useMemo(
    () => ({
      items: {
        rename_column: {
          disabled: hasPendingThreads,
          name: t`Rename Column`,
          callback: renameColumn,
          hidden: () => poweredByTemplate,
        },
        '---------': {
          hidden: () => !notDropdown || poweredByTemplate,
        },
        remove_col: {
          disabled: hasPendingThreads,
          name: t`Remove Column`,
          hidden: () => !notDropdown || poweredByTemplate,
        },
        col_left: {
          disabled: hasPendingThreads,
          name: t`Insert Column Before`,
          callback: renameNewColumn,
          hidden: () => !notDropdown || poweredByTemplate,
        },
        remove_row: {
          disabled: hasPendingThreads,
          name: t`Remove Row`,
          hidden: () => poweredByTemplate,
          callback: afterRemoveRows,
        },
        row_above: {
          disabled: hasPendingThreads,
          name: t`Insert Row`,
          hidden: () => poweredByTemplate,
          callback: afterAddRow,
        },
        move_column_to_start: {
          disabled: hasPendingThreads,
          name: t`Move Column To Start`,
          callback: moveToStart,
          hidden: () => !notDropdown,
        },
        divider_2: '---------',
        message: {
          hidden: () => !hasPendingThreads,
          renderer: () => {
            const messageContainer = document.createElement('div')
            const message = document.createElement('span')

            messageContainer.style.width = '195px'
            messageContainer.style.padding = '7px 6px 9px 17px'

            message.textContent = t`List actions are disabled while pending suggestions are resolved`
            message.style.cssText =
              'font-size: 12px; font-style: italic; color: black; font-weight: 400; word-break: break-word; white-space: normal; display: inline-block;'

            messageContainer.appendChild(message)

            return messageContainer
          },
        },
      },
    }),
    [renameColumn, renameNewColumn, afterRemoveRows, afterAddRow, moveToStart],
  )

  const afterChange = changes => {
    if (changes?.length && !searchTerm) {
      const row = 0
      const column = 1
      const newValue = 3
      setList(old => {
        const newColumnLength = Math.max(...changes.map(change => change[1]))
        const copy = [...old.list_columns]
        const copyLength = copy.length // cache length since we're mutating copy and it will change.

        // add needed columns that don't exist yet
        for (let i = 0; i <= newColumnLength - copyLength; i++) {
          copy.push({
            id: tempIdGenerator('lc'),
            name: '',
            precedence: i + copyLength,
            list_values: [],
          })
        }

        // populate grid with values from changes array
        changes.forEach((change, index) => {
          // there is no guarantee that row_number == array index, we must search for it
          const oldCellIndex = copy[change[column]]?.list_values.findIndex(
            it => it?.row_number === change[row],
          )
          const oldCell = copy[change[column]]?.list_values[oldCellIndex]

          // create the new column if needed
          if ((oldCell === undefined || oldCell === null || oldCell === '') && change[newValue]) {
            copy[change[column]] = copy[change[column]] || {
              id: tempIdGenerator('lc'),
              name: '',
              precedence: index,
              list_values: [],
            }
          }

          // remove the old cell
          if (oldCellIndex !== -1) {
            copy[change[column]].list_values.splice(oldCellIndex, 1)
          }

          // add the new cell
          copy[change[column]].list_values.push({
            id: oldCell?.id || tempIdGenerator('lv'),
            row_number: change[row],
            value: change[newValue] || (row === 0 ? '' : change[newValue]),
          })
        })

        const listColumnsWithNewColumn = copyLength
          ? copy.map(col => ({
              ...col,
              list_values: col.list_values.map(value => ({ ...value })),
            }))
          : [newColumn]

        onChange()
        return {
          ...old,
          list_columns: listColumnsWithNewColumn,
        }
      })
    }
  }

  const afterRemoveCol = useCallback(
    (index, amount) => {
      setList(old => {
        const copy = [...old.list_columns]
        copy.splice(index, amount)
        const listColumnsWithNewColumn = copy.map((lc, idx) => ({
          ...lc,
          precedence: idx,
        }))
        return {
          ...old,
          list_columns: listColumnsWithNewColumn,
        }
      })
    },
    [setList],
  )

  const confirmRename = useCallback(() => {
    setOpen(false)
    setIsNewColumDialog(false)
    setList(old => {
      const copy = [...old.list_columns]
      copy[renamingColumn] = {
        ...copy[renamingColumn],
        name: newName,
        id: copy[renamingColumn]?.id || tempIdGenerator('lc'),
        list_values: copy[renamingColumn]?.list_values || [],
      }

      return {
        ...old,
        list_columns: copy,
      }
    })
  }, [renamingColumn, newName])

  const handleCloseDialog = useCallback(() => {
    setOpen(false)
    setIsNewColumDialog(false)
  }, [])

  const hotSettings = {
    data,
    width: gridContainer.clientWidth,
    height: gridContainer.clientHeight,
    licenseKey: LICENSE_KEY,
    colWidths: 250,
    contextMenu: isApproverMode ? contextMenu : false,
    editor: ListEditor,
    fillHandle: true,
    manualColumnFreeze: poweredByTemplate,
    manualColumnMove: poweredByTemplate,
    manualColumnResize: true,
    maxCols: !notDropdown ? 1 : Infinity,
    minSpareCols: 1,
    minSpareRows: 10,
    readOnly: readOnly || !!poweredByTemplate,
    rowHeaders: true,
    rowHeights: 40,
    startCols: 1,
    copyPaste: {
      columnsLimit: 100,
      rowsLimit: 10000,
    },
    afterChange,
    afterGetColHeader: addColumnHeaderDataAttributes,
    afterGetRowHeader: addRowHeaderDataAttributes,
    afterRemoveCol,
    beforePaste: (data, _cords) => {
      const table = tableRef?.current?.hotInstance
      const selection = table.getSelected()
      const singleSetSelection = selection.length === 1

      // We are currently only supporting a single selection (not skipped rows in the selection).
      if (singleSetSelection) {
        const [startRow, _startCol, endRow, _endCol] = selection[0]
        const isMultipleRowsSelected = endRow > 0 && startRow !== endRow
        const positiveNumSelection = Math.abs(endRow - startRow)
        const numberOfRowsSelected = positiveNumSelection + 1 // 1 = include starting row

        if (isMultipleRowsSelected) {
          data.splice(numberOfRowsSelected)
        }
      }
    },
    colHeaders: index => {
      // FIXME: this code fixes a bug where the column name is not present in the list
      // we expect it to be, and we should not be setting state in a display function like this
      // TERRIBLE PRACTICE, GO FIX THE ROOT CAUSE!
      // CV-9906
      let columnName = columnHeaders[index]
      if (!columnName) {
        const indexDisplay = index + 1
        columnName = t`Column ${indexDisplay}`
        setList(old => {
          const copy = [...old.list_columns]
          copy[index] = {
            ...copy[index],
            name: columnName,
            id: copy[index]?.id || tempIdGenerator('lc'),
            list_values: copy[index]?.list_values || [],
          }

          return {
            ...old,
            list_columns: copy,
          }
        })
      }
      return columnName
    },
    renderer: TextRendererWithDataAttributes,
    tabMoves: () => {
      const tab = tableRef?.current?.hotInstance
      const selected = tab.getSelected()
      const countCols = tab.countCols()
      const selectedStart = selected[0]

      // if we are on the end and tabbing
      if (!event.shiftKey && selectedStart[1] === countCols - 1) {
        return { row: 1, col: -(countCols - 1) }
      }

      // if we are on the first and going backwards
      if (event.shiftKey && selectedStart[1] === 0) {
        return { row: 1, col: -(countCols - 1) }
      }

      return { row: 0, col: 1 }
    },
    cells: () => ({
      renderer: canUseLiveblocks ? ButtonRenderer : undefined,
    }),
    afterOnCellMouseDown: canUseLiveblocks
      ? (event, coords) => {
          const commentButton = event.target.closest('.cell-button')
          const listValueId = getListValueId(list, coords.row, coords.col)

          if (commentButton) {
            event.stopPropagation()
            const rect = commentButton.getBoundingClientRect()
            const position = getSafeDialogPosition(rect)

            if (listValueId) {
              openCommentDialog(listValueId, position)
            }
            return
          }

          if (listValueId) {
            setActiveCommentTarget({ id: listValueId, position: null })
          } else {
            setActiveCommentTarget(null)
          }
        }
      : undefined,
    openCommentDialog,
  }

  return (
    <>
      {canUseLiveblocks && activeCommentTarget?.id && (
        <CommentDialog
          activeCommentTarget={activeCommentTarget}
          onClose={() => setActiveCommentTarget(null)}
          list={list}
          tableRef={tableRef}
          threads={threads}
          updateLocalThreadStatus={updateLocalThreadStatus}
          queueThreadResolution={queueThreadResolution}
          createThread={createThread}
          isApproverMode={isApproverMode}
        />
      )}
      <HotTable ref={tableRef} settings={hotSettings} />
      <ColumnDialog
        open={open}
        title={renameDialogTitle}
        handleCloseDialog={handleCloseDialog}
        handleConfirmDialog={confirmRename}
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t`Renaming this column will not break/change any fields mapped to this column.`}
          </DialogContentText>
          <TextField
            id="renameField"
            margin="dense"
            variant="outlined"
            autoFocus
            onChange={e => {
              e.stopPropagation()
              setNewName(e.target.value)
            }}
          />
        </DialogContent>
      </ColumnDialog>
      <ColumnDialog
        open={isNewColumDialog}
        title={t`Name new column`}
        handleCloseDialog={handleCloseDialog}
        handleConfirmDialog={event => {
          insertColumn(event, selectionCol)
        }}
        confirmLabel={t`Add Column`}
      >
        <DialogContent>
          <TextField
            id="newField"
            margin="dense"
            variant="outlined"
            autoFocus
            onChange={e => {
              e.stopPropagation()
              setNewName(e.target.value)
            }}
          />
        </DialogContent>
      </ColumnDialog>
    </>
  )
}

ListTableCore.propTypes = {
  readOnly: bool,
  list: object,
  type: string,
  setList: func.isRequired,
  onChange: func,
  poweredByTemplate: object,
  searchTerm: string,
  setTableRef: func.isRequired,
  threads: array,
  activeCommentTarget: object,
  setActiveCommentTarget: func,
  checkPendingStatus: func,
  updateLocalThreadStatus: func,
  queueThreadResolution: func,
  queuedResolutionsRef: object,
  createThread: func,
  editThreadMetadata: func,
  isApproverMode: bool,
  tableRef: object,
}
